import {
  LayoutDashboard,
  Users,
  Camera,
  Settings,
  FileText,
  LogOut,
  Menu,
  X,
  User,
  Shield,
  Database,
  Tag,
  ClipboardCheck,
  TrendingUp,
  FileSearch,
  Cpu,
  MapPin,
  MessageSquare,
  BarChart2,
  Film,
  Archive,
  Users2,
  Activity,
  UserCheck,
  Phone,
  FlaskConical,
  Server,
  ListChecks,
  ChevronDown,
  ChevronRight,
  Check,
  Loader2,
  BookOpen,
  Download,
  Eye,
  Network,
  Bell,
  Monitor,
  Megaphone,
  Rocket,
  Upload,
} from 'lucide-react';
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';

import { PERMISSIONS } from '../../constants/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../hooks/api/useOrgMembers';
import { useCanReadAdminData } from '../../hooks/useCanReadAdminData';
import { usePermission } from '../../hooks/usePermission';
import { useTenants } from '../../hooks/useTenants';
import VersionBadge from '../common/VersionBadge';
import { NotificationBell } from '../notifications/NotificationBell';
import { RoleBadge } from '../org/RoleBadge';

// Breakpoint for auto-collapsing sidebar (when content area becomes too narrow)
const SIDEBAR_WIDTH = 256; // 64 * 4 = 256px (w-64)
const MIN_CONTENT_WIDTH = 600; // Minimum content area width before auto-collapse
const AUTO_COLLAPSE_BREAKPOINT = SIDEBAR_WIDTH + MIN_CONTENT_WIDTH; // ~856px

// UUID_REGEX: detect real Logto org UUIDs vs Logto M2M org IDs (short alphanumeric).
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// KNOWN_TENANT_DISPLAY_NAMES maps Logto M2M org IDs (short alphanumeric like
// "6x28qijomtku") to friendly display names. We can't rely on the backend
// /admin/tenants response to do this because tenants table stores canonical
// UUIDs while the JWT carries Logto org IDs, so the lookup in
// `tenants.find(t => t.id === tenantId)` always misses.
//
// Keep this in sync with the seed row in
// services/main-service/migrations/20260810000000_create_tenant_id_map.sql.
const KNOWN_TENANT_DISPLAY_NAMES = {
  '6x28qijomtku': 'Sys9',
  // Dev/mock-mode tenant UUID (see src/mocks/handlers/tenant.handlers.ts).
  '00000000-0000-4000-8000-000000000001': 'Dev Tenant',
};

// formatTenantLabel turns a raw tenant_id into a human-readable label when
// the tenant name isn't available. UUIDs are shown as first-8-chars;
// Logto M2M org IDs (e.g. "6x28qijomtku") are labeled with a friendly
// fallback so users don't see opaque alphanumeric strings.
const formatTenantLabel = id => {
  if (!id) return '';
  // Highest priority: known Logto M2M org IDs with explicit display names.
  if (KNOWN_TENANT_DISPLAY_NAMES[id]) {
    return KNOWN_TENANT_DISPLAY_NAMES[id];
  }
  if (UUID_REGEX.test(id)) return `${id.slice(0, 8)}…`;
  if (/^[a-z0-9]{8,16}$/i.test(id))
    return `Logto Org ${id.slice(0, 6).toUpperCase()}`;
  return id;
};

// Navigation configuration moved outside component to remain stable.
// Groups are aligned 1:1 with backend modules (services/main-service/modules/*)
// wherever practical; a group only bundles more than one module when those
// modules are tightly coupled in the code (e.g. video + facedetection power
// the same clip/face review workflow). Where a group still spans modules,
// items carry a `section` label so the sidebar breaks them into sub-headers
// instead of presenting them as one undifferentiated list.
// Persisted collapse state key: 'admin-nav-collapsed-groups'.
const navigationGroups = [
  {
    items: [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: LayoutDashboard,
        show: true,
      },
    ],
  },
  {
    title: 'Sites & Devices',
    items: [
      {
        name: 'Sites',
        href: '/sites',
        icon: MapPin,
        show: true,
        section: 'Sites & Agents',
      },
      {
        name: 'Agents',
        href: '/agents',
        icon: Cpu,
        show: true,
        section: 'Sites & Agents',
      },
      {
        name: 'Pending Agents',
        href: '/agents/pending',
        icon: UserCheck,
        show: true,
        section: 'Sites & Agents',
      },
      {
        name: 'Cameras',
        href: '/cameras',
        icon: Camera,
        show: true,
        section: 'Devices',
      },
    ],
  },
  {
    title: 'Video & Face Recognition',
    items: [
      {
        name: 'Clip Archive',
        href: '/video/clips',
        icon: Archive,
        show: true,
        section: 'Video',
      },
      {
        name: 'Clip Testing',
        href: '/video/testing',
        icon: Film,
        show: true,
        section: 'Video',
      },
      {
        name: 'Evaluation Hub',
        href: '/video/evaluation-hub',
        icon: FileSearch,
        show: true,
        section: 'Video',
      },
      {
        name: 'Video Analytics',
        href: '/video/analytics',
        icon: Activity,
        show: true,
        section: 'Video',
        badge: 'Soon',
      },
      {
        name: 'Face Events',
        href: '/video/events',
        icon: Users2,
        show: true,
        section: 'Face Recognition',
      },
      {
        name: 'Alarm Events',
        href: '/video/alarm-events',
        icon: Bell,
        show: true,
        section: 'Face Recognition',
      },
      {
        name: 'Face Members',
        href: '/video/face-members',
        icon: UserCheck,
        show: true,
        section: 'Face Recognition',
      },
      {
        name: 'Face Config',
        href: '/video/face-config',
        icon: Settings,
        show: true,
        section: 'Face Recognition',
      },
    ],
  },
  {
    title: 'AI & ML',
    items: [
      {
        name: 'ML Models',
        href: '/ml/models',
        icon: Database,
        show: true,
        section: 'ML',
      },
      {
        name: 'A/B Tests',
        href: '/ml/ab-tests',
        icon: TrendingUp,
        show: true,
        section: 'ML',
      },
      {
        name: 'HITL QA',
        href: '/hitl/qa',
        icon: ClipboardCheck,
        show: true,
        section: 'HITL',
      },
      {
        name: 'Data Labeling',
        href: '/hitl/data',
        icon: Tag,
        show: true,
        section: 'HITL',
      },
      {
        name: 'Training',
        href: '/hitl/training',
        icon: TrendingUp,
        show: true,
        section: 'HITL',
      },
    ],
  },
  {
    title: 'KYC Verification',
    items: [{ name: 'KYC Review', href: '/kyc-review', icon: FileText }],
  },
  {
    title: 'Knowledge Base',
    items: [
      {
        name: 'Documents',
        href: '/knowledge',
        icon: BookOpen,
        show: true,
        section: 'Knowledge',
      },
      {
        name: 'Entities',
        href: '/knowledge/entities',
        icon: Network,
        show: true,
        section: 'Knowledge',
      },
      {
        name: 'Pages',
        href: '/knowledge/pages',
        icon: FileText,
        show: true,
        section: 'Knowledge',
      },
      {
        name: 'Ingestion Sources',
        href: '/knowledge/ingestion',
        icon: Download,
        show: true,
        section: 'Knowledge',
      },
      {
        name: 'งานนำเข้า',
        href: '/knowledge/ingestion/jobs',
        icon: ListChecks,
        show: true,
        section: 'Knowledge',
      },
    ],
  },
  {
    title: 'Market Intelligence',
    items: [
      { name: 'Competitors', href: '/market-intel', icon: Eye, show: true },
      {
        name: 'Vendor Products',
        href: '/market-intel/vendor-products',
        icon: Database,
        show: true,
      },
      {
        name: 'Price Comparison',
        href: '/market-intel/price-comparison',
        icon: TrendingUp,
        show: true,
      },
      {
        name: 'Daily Report',
        href: '/market-intel/reports/daily',
        icon: FileText,
        show: true,
      },
    ],
  },
  {
    title: 'Products',
    items: [
      {
        name: 'Product Catalog',
        href: '/products',
        icon: ClipboardCheck,
        show: true,
      },
      {
        name: 'Vector Search',
        href: '/products/search',
        icon: FileSearch,
        show: true,
      },
    ],
  },
  {
    title: 'Chatbot & Messaging',
    items: [
      { name: 'Chatbot', href: '/chatbot', icon: MessageSquare, show: true },
      {
        name: 'Skill Candidates',
        href: '/chatbot/skills/candidates',
        icon: ListChecks,
        show: true,
      },
      {
        name: 'Chatbot Analytics',
        href: '/chatbot/analytics',
        icon: BarChart2,
        show: true,
      },
      { name: 'LINE Bots', href: '/line-bot', icon: MessageSquare, show: true },
      {
        name: 'LINE Groups',
        href: '/line-bot/groups',
        icon: Users,
        show: true,
      },
      {
        name: 'Telegram Bots',
        href: '/telegram-bot',
        icon: MessageSquare,
        show: true,
      },
    ],
  },
  {
    title: 'Call Center',
    items: [
      {
        name: 'Quality Review POC',
        href: '/calls/review',
        icon: ListChecks,
        show: true,
      },
      {
        name: 'Daily POC Report',
        href: '/calls/reports/daily',
        icon: ListChecks,
        show: true,
      },
      {
        name: 'Recordings',
        href: '/calls/recordings',
        icon: Phone,
        show: true,
      },
      {
        name: 'Test Console',
        href: '/calls/test',
        icon: FlaskConical,
        show: true,
      },
      { name: 'UCM Deployments', href: '/calls/ucm', icon: Server, show: true },
      {
        name: 'Pipeline Queue',
        href: '/calls/queue',
        icon: ListChecks,
        show: true,
      },
    ],
  },
  {
    title: 'Signage',
    items: [
      { name: 'Signage', href: '/signage', icon: Monitor, show: true },
      {
        name: 'Announcements',
        href: '/signage/announcements',
        icon: Megaphone,
        show: true,
      },
      { name: 'Playlists', href: '/signage/playlists', icon: Film, show: true },
    ],
  },
  {
    title: 'OTA Updates',
    items: [
      {
        name: 'Campaigns',
        href: '/ota/campaigns',
        icon: Rocket,
        show: true,
      },
      {
        name: 'New Campaign',
        href: '/ota/campaigns/new',
        icon: Rocket,
        show: true,
      },
      {
        name: 'Package Upload',
        href: '/ota/packages',
        icon: Upload,
        show: true,
      },
      {
        name: 'Alert Configuration',
        href: '/ota/alerts',
        icon: Bell,
        show: true,
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        name: 'Merchants',
        href: '/admin/merchants',
        icon: Database,
        show: true,
        section: 'Platform',
      },
      {
        name: 'Audit Logs',
        href: '/audit/logs',
        icon: FileSearch,
        show: true,
        adminData: true,
        section: 'Platform',
      },
      {
        name: 'System Settings',
        href: '/settings',
        icon: Settings,
        show: true,
        adminData: true,
        section: 'Platform',
      },
      {
        name: 'Organization Members',
        href: '/members',
        icon: Users,
        show: true,
        section: 'Access',
      },
    ],
  },
];

const navigationHrefs = navigationGroups.flatMap(group =>
  group.items.map(item => item.href).filter(Boolean)
);

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('admin-nav-collapsed-groups');
      if (saved) return JSON.parse(saved);
    } catch {
      // Unreadable saved state: use the defaults.
    }
    return {};
  });
  const [switchingTenantId, setSwitchingTenantId] = useState(null);
  const tenantMenuRef = useRef(null);
  const { user, tenantId, logout, switchTenant } = useAuth();
  const location = useLocation();
  const { data: canViewKYC } = usePermission('kyc:verification:read');
  // Spec 2.6: the import-jobs entry needs knowledge:ingest.
  const { data: canIngestKnowledge } = usePermission(
    PERMISSIONS.KNOWLEDGE_INGEST
  );
  const canReadAdminData = useCanReadAdminData();
  const { data: tenants = [], isLoading: tenantsLoading } = useTenants();

  const currentTenant = useMemo(() => {
    if (!Array.isArray(tenants)) return null;
    return tenants.find(tenant => tenant.id === tenantId) || null;
  }, [tenants, tenantId]);

  // The Logto organization (name + my role in it) when the server can name
  // it; the tenant list is the fallback for deployments without it.
  const { data: organization } = useOrganization();
  const tenantDisplayName =
    organization?.name ||
    currentTenant?.name ||
    formatTenantLabel(tenantId) ||
    '';

  // Memoize active navigation groups based on permissions
  const memoizedNavigationGroups = useMemo(() => {
    return navigationGroups
      .map(group => ({
        ...group,
        items: group.items
          .map(item => {
            if (item.name === 'KYC Review') {
              return { ...item, show: !!canViewKYC };
            }
            if (item.name === 'งานนำเข้า') {
              return { ...item, show: !!canIngestKnowledge };
            }
            if (item.adminData) {
              return { ...item, show: canReadAdminData };
            }
            return item;
          })
          .filter(item => item.show !== false),
      }))
      .filter(group => group.items.length > 0);
  }, [canViewKYC, canIngestKnowledge, canReadAdminData]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const handleSwitchTenant = useCallback(
    async targetTenantId => {
      if (!targetTenantId || targetTenantId === tenantId) {
        setTenantMenuOpen(false);
        return;
      }

      setSwitchingTenantId(targetTenantId);
      try {
        await switchTenant(targetTenantId);
      } catch (error) {
        console.error('Failed to switch tenant:', error);
      } finally {
        setSwitchingTenantId(null);
        setTenantMenuOpen(false);
      }
    },
    [switchTenant, tenantId]
  );

  const isActive = useCallback(
    href => {
      if (href === '/dashboard') return location.pathname === href;
      if (!location.pathname.startsWith(href)) return false;
      // A more specific item wins: /line-bot/groups highlights LINE Groups,
      // not LINE Bots as well.
      return !navigationHrefs.some(
        other =>
          other.length > href.length &&
          other.startsWith(href) &&
          location.pathname.startsWith(other)
      );
    },
    [location.pathname]
  );

  const toggleGroup = useCallback(title => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try {
        localStorage.setItem(
          'admin-nav-collapsed-groups',
          JSON.stringify(next)
        );
      } catch {
        // Storage can be unavailable; keep the in-memory state.
      }
      return next;
    });
  }, []);

  // Handle mobile sidebar auto-close on navigation
  useEffect(() => {
    if (location.pathname) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        tenantMenuRef.current &&
        !tenantMenuRef.current.contains(event.target)
      ) {
        setTenantMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (tenantId) {
      setTenantMenuOpen(false);
    }
  }, [tenantId]);

  // Auto-collapse sidebar when window is too narrow
  useEffect(() => {
    const handleResize = () => {
      const shouldCollapse = window.innerWidth < AUTO_COLLAPSE_BREAKPOINT;
      setSidebarCollapsed(shouldCollapse);
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className='fixed inset-0 z-40 lg:hidden'>
          <button
            type='button'
            className='fixed inset-0 bg-black bg-opacity-25'
            onClick={() => setSidebarOpen(false)}
          >
            <span className='sr-only'>Close sidebar overlay</span>
          </button>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 lg:top-16 lg:h-[calc(100vh-4rem)] bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
          sidebarCollapsed ? '' : 'lg:translate-x-0'
        } ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col h-full`}
      >
        <div className='flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0'>
          <div className='flex items-center'>
            <Shield className='h-8 w-8 text-blue-600' />
            <span className='ml-2 text-xl font-bold text-gray-900'>
              AI Service Admin
            </span>
          </div>
          <button
            type='button'
            onClick={() => setSidebarOpen(false)}
            className='lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500'
          >
            <X className='h-6 w-6' />
          </button>
        </div>

        <nav className='pt-4 px-3 flex-1 overflow-y-auto'>
          <div className='space-y-4 pb-2'>
            {memoizedNavigationGroups.map((group, groupIdx) => {
              const isGroupCollapsed = group.title
                ? !!collapsedGroups[group.title]
                : false;
              return (
                <div key={group.title || group.items[0]?.name || groupIdx}>
                  {group.title && (
                    <button
                      type='button'
                      onClick={() => toggleGroup(group.title)}
                      className='w-full flex items-center justify-between px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 hover:text-gray-600 transition-colors focus:outline-none'
                    >
                      <span>{group.title}</span>
                      {isGroupCollapsed ? (
                        <ChevronRight className='h-3 w-3' />
                      ) : (
                        <ChevronDown className='h-3 w-3' />
                      )}
                    </button>
                  )}
                  {!isGroupCollapsed && (
                    <div className='space-y-0.5'>
                      {group.items.map((item, itemIdx) => {
                        const Icon = item.icon;
                        const showSectionLabel =
                          item.section &&
                          item.section !== group.items[itemIdx - 1]?.section;
                        return (
                          <React.Fragment key={item.name}>
                            {showSectionLabel && (
                              <div
                                className={`px-3 text-[10px] font-medium text-gray-400 uppercase tracking-wider ${
                                  itemIdx === 0 ? 'mb-1' : 'mt-2 mb-1'
                                }`}
                              >
                                {item.section}
                              </div>
                            )}
                            <Link
                              to={item.href}
                              onClick={() => setSidebarOpen(false)}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                                isActive(item.href)
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <Icon
                                className={`mr-3 h-5 w-5 flex-shrink-0 ${
                                  isActive(item.href)
                                    ? 'text-blue-500'
                                    : 'text-gray-400 group-hover:text-gray-500'
                                }`}
                              />
                              {item.name}
                              {item.badge && (
                                <span className='ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700'>
                                  {item.badge}
                                </span>
                              )}
                            </Link>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* User info and logout */}
        <div className='mt-auto p-4 border-t border-gray-200'>
          <div className='flex items-center mb-3'>
            <div className='flex-shrink-0'>
              <div className='h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center'>
                <User className='h-4 w-4 text-white' />
              </div>
            </div>
            <div className='ml-3 flex-1 min-w-0'>
              <p className='text-sm font-medium text-gray-900 truncate'>
                {user?.username || 'Admin User'}
              </p>
              <p className='text-xs text-gray-500 truncate'>
                {user?.adminRole || 'Administrator'}
              </p>
            </div>
          </div>
          <button
            type='button'
            onClick={handleLogout}
            className='flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150'
          >
            <LogOut className='mr-3 h-4 w-4' />
            Sign out
          </button>
          <VersionBadge />
        </div>
      </div>

      {/* Main content */}
      <div>
        {/* Top bar */}
        <div
          className={`fixed top-0 left-0 right-0 z-10 flex h-16 bg-white shadow-sm border-b border-gray-200 ${sidebarCollapsed ? '' : 'lg:pl-64'}`}
        >
          <button
            type='button'
            onClick={() => setSidebarOpen(true)}
            className={`${sidebarCollapsed ? '' : 'lg:hidden'} px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500`}
          >
            <Menu className='h-6 w-6' />
          </button>

          <div className='flex-1 px-4 flex justify-between items-center'>
            <div className='flex-1' />
            <div className='ml-4 flex items-center md:ml-6 space-x-4'>
              <div className='text-sm text-gray-700'>
                Welcome back, {user?.username || 'Admin'}
              </div>

              <div className='relative' ref={tenantMenuRef}>
                <button
                  type='button'
                  onClick={() => setTenantMenuOpen(open => !open)}
                  className='flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-left shadow-sm transition-colors hover:bg-gray-50'
                >
                  <div className='min-w-0'>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
                      Tenant
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className='max-w-[14rem] truncate text-sm font-medium text-gray-900'>
                        {tenantDisplayName || 'All tenants'}
                      </span>
                      {organization?.role && (
                        <span className='flex items-center gap-1 text-xs text-gray-600'>
                          <span className='sr-only'>คุณเป็น</span>
                          <RoleBadge role={organization.role} />
                        </span>
                      )}
                      {organization?.platform_admin && (
                        <span className='whitespace-nowrap rounded-full bg-foreground px-2 py-0.5 text-xs font-medium text-background'>
                          Platform admin
                        </span>
                      )}
                    </div>
                  </div>
                  {/* No short-id badge: when no friendly name resolves the
                      label itself already falls back to the short id, so a
                      badge would only echo the same characters twice. */}
                  <ChevronDown className='h-4 w-4 text-gray-400' />
                </button>

                {tenantMenuOpen && (
                  <div className='absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg'>
                    <div className='border-b border-gray-100 px-4 py-3'>
                      <p className='text-sm font-medium text-gray-900'>
                        Switch tenant
                      </p>
                      <p className='text-xs text-gray-500'>
                        Choose a tenant to refresh the app context.
                      </p>
                    </div>

                    <div className='max-h-80 overflow-y-auto py-1'>
                      {tenantsLoading ? (
                        <div className='flex items-center gap-2 px-4 py-3 text-sm text-gray-500'>
                          <Loader2 className='h-4 w-4 animate-spin' />
                          Loading tenants...
                        </div>
                      ) : tenants.length > 0 ? (
                        tenants.map(tenant => {
                          const isActive = tenant.id === tenantId;
                          const isSwitching = switchingTenantId === tenant.id;

                          return (
                            <button
                              key={tenant.id}
                              type='button'
                              onClick={() => handleSwitchTenant(tenant.id)}
                              disabled={isActive || isSwitching}
                              className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors ${
                                isActive
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'hover:bg-gray-50'
                              } ${
                                isSwitching ? 'opacity-70' : ''
                              } disabled:cursor-not-allowed`}
                            >
                              <div className='min-w-0'>
                                <div className='flex items-center gap-2'>
                                  <span className='truncate text-sm font-medium text-gray-900'>
                                    {tenant.name}
                                  </span>
                                  {isActive && (
                                    <Check className='h-4 w-4 text-blue-600' />
                                  )}
                                </div>
                                <div className='truncate text-xs text-gray-500'>
                                  {tenant.id}
                                </div>
                              </div>

                              <div className='flex items-center gap-2 pl-4'>
                                {isSwitching && (
                                  <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
                                )}
                                <span className='rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-600'>
                                  {tenant.status || 'active'}
                                </span>
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className='px-4 py-3 text-sm text-gray-500'>
                          No tenants available.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notification Center */}
              <div className='mr-2'>
                <NotificationBell />
              </div>

              <button
                type='button'
                onClick={handleLogout}
                className='flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors'
              >
                <LogOut className='h-4 w-4' />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className={`flex-1 pt-16 ${sidebarCollapsed ? '' : 'lg:pl-64'}`}>
          <div className='pb-6'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 md:px-8'>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
